"use client"

import * as React from "react"
import { cn } from "@/lib/utils"

export interface InternalInputProps extends React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
    startIcon?: React.ReactNode
    multiline?: boolean
    rows?: number
    label?: string
    error?: string
    required?: boolean
}

const InternalInput = React.forwardRef<HTMLInputElement | HTMLTextAreaElement, InternalInputProps>(
    ({ className, type, startIcon, multiline, rows, label, error, required, ...props }, ref) => {
        const inputContent = multiline ? (
            <textarea
                className={cn(
                    "flex w-full rounded-xl border-0 bg-slate-100 px-3 py-2 text-xs text-slate-600 placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-indigo-500/20 disabled:cursor-not-allowed disabled:opacity-50 min-h-[60px] resize-none shadow-none transition-all hover:bg-slate-200/50",
                    error && "border border-red-500 bg-red-50 focus-visible:ring-red-500/20",
                    className
                )}
                rows={rows || 3}
                ref={ref as React.Ref<HTMLTextAreaElement>}
                {...props as React.TextareaHTMLAttributes<HTMLTextAreaElement>}
            />
        ) : (
            <div className="relative w-full">
                {startIcon && (
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 flex items-center justify-center pointer-events-none">
                        {startIcon}
                    </div>
                )}
                <input
                    type={type}
                    className={cn(
                        "flex h-10 w-full rounded-xl border-0 bg-slate-100 px-3 py-1 text-xs shadow-none transition-all file:border-0 file:bg-transparent file:text-xs file:font-medium placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-indigo-500/20 disabled:cursor-not-allowed disabled:opacity-50 md:text-xs hover:bg-slate-200/50",
                        startIcon && "pl-9",
                        error && "border border-red-500 bg-slate-100 focus-visible:ring-red-500/20",
                        className
                    )}
                    ref={ref as React.Ref<HTMLInputElement>}
                    {...props as React.InputHTMLAttributes<HTMLInputElement>}
                />
            </div>
        )

        if (label) {
            return (
                <div className="space-y-1.5 w-full">
                    <label className="text-xs font-semibold text-slate-500 pb-0.5 block">
                        {label} {required && <span className="text-red-500">*</span>}
                    </label>
                    {inputContent}
                    {error && (
                        <p className="text-xs text-red-500 mt-1">{error}</p>
                    )}
                </div>
            )
        }

        return (
            <div className="w-full">
                {inputContent}
                {error && (
                    <p className="text-xs text-red-500 mt-1">{error}</p>
                )}
            </div>
        )
    }
)

InternalInput.displayName = "InternalInput"

export { InternalInput }
